import { FormControl, FormField, FormItem, FormMessage, FormDescription } from '@/components/ui/form';
import { useFormContext, useWatch } from 'react-hook-form';
import BridgeRouteSelector from '@/features/EdgeLink/BridgeRouteSelector';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Network, ChevronDown, Check, Loader2, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosClientAdmin } from '@/lib/auth';
import { useToast } from '@/hooks/use-toast';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface NetworkSettingsFormProps {
  /** The site's current default route ID, if any */
  siteDefaultRouteId?: string | null;
  /** Whether to show the "set as site default" option */
  showSetSiteDefault?: boolean;
  /** Customer ID for updating site default */
  customerId?: string;
  /** Site ID for updating site default */
  siteId?: string;
}

export const NetworkSettingsForm = ({
  siteDefaultRouteId,
  showSetSiteDefault = true,
  customerId,
  siteId,
}: NetworkSettingsFormProps) => {
  const form = useFormContext();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const routeNamespaceId = useWatch({ control: form.control, name: 'routeNamespaceId' });

  // Track if EdgeLink is enabled (has a route selected or user explicitly enabled it)
  const [isEnabled, setIsEnabled] = useState(Boolean(routeNamespaceId));
  // Track the current site default locally for optimistic updates
  const [localSiteDefault, setLocalSiteDefault] = useState(siteDefaultRouteId);

  // Sync enabled state with form value
  useEffect(() => {
    if (routeNamespaceId) {
      setIsEnabled(true);
    }
  }, [routeNamespaceId]);

  // Sync local site default with prop
  useEffect(() => {
    setLocalSiteDefault(siteDefaultRouteId);
  }, [siteDefaultRouteId]);

  // Mutation to set site default
  const setSiteDefaultMutation = useMutation({
    mutationFn: async (routeId: string) => {
      if (!customerId || !siteId) {
        throw new Error('Customer and site are required');
      }
      await axiosClientAdmin.post(`/Customer/${customerId}/project/${siteId}`, {
        defaultRouteNamespaceId: routeId,
      });
      return routeId;
    },
    onSuccess: (routeId) => {
      setLocalSiteDefault(routeId);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['customer-sites'] });
      queryClient.invalidateQueries({ queryKey: ['edgelink'] });
      toast({
        title: 'Site default updated',
        description: 'This EdgeLink route is now the default for this site.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to update site default',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Mutation to clear site default
  const clearSiteDefaultMutation = useMutation({
    mutationFn: async () => {
      if (!customerId || !siteId) {
        throw new Error('Customer and site are required');
      }
      await axiosClientAdmin.post(`/Customer/${customerId}/project/${siteId}`, {
        defaultRouteNamespaceId: '00000000-0000-0000-0000-000000000000',
      });
    },
    onSuccess: () => {
      setLocalSiteDefault(null);
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['customer-sites'] });
      queryClient.invalidateQueries({ queryKey: ['edgelink'] });
      toast({
        title: 'Site default removed',
        description: 'This site no longer has a default EdgeLink route.',
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Failed to remove site default',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Determine states
  const hasSelectedRoute = Boolean(routeNamespaceId);
  const isCurrentSiteDefault = hasSelectedRoute && routeNamespaceId === localSiteDefault;
  const canSetAsDefault = showSetSiteDefault && hasSelectedRoute && !isCurrentSiteDefault && customerId && siteId;

  const handleToggle = (enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      // Clear the route when disabling
      form.setValue('routeNamespaceId', '');
    }
  };

  const handleSetAsDefault = () => {
    if (routeNamespaceId) {
      setSiteDefaultMutation.mutate(routeNamespaceId);
    }
  };

  const handleClearSiteDefault = () => {
    clearSiteDefaultMutation.mutate();
  };

  return (
    <Collapsible open={isEnabled} onOpenChange={handleToggle}>
      <div className="rounded-lg border border-[#2E3835] bg-[#17221F]/50">
        <CollapsibleTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors rounded-lg"
          >
            <div className="flex items-center gap-3">
              <Network className={cn(
                "h-4 w-4 transition-colors",
                isEnabled ? "text-lime" : "text-muted-foreground"
              )} />
              <div className="flex flex-col">
                <span className="text-sm font-medium">EdgeLink Connection</span>
                <span className="text-xs text-muted-foreground">
                  Route traffic through a secure tunnel
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={isEnabled}
                onCheckedChange={handleToggle}
                onClick={(e) => e.stopPropagation()}
                className='data-[state=unchecked]:bg-muted/20'
              />
              <ChevronDown
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isEnabled && "rotate-180"
                )}
              />
            </div>
          </button>
        </CollapsibleTrigger>

        <CollapsibleContent>
          <div className="px-4 pb-4 space-y-3 border-t border-[#2E3835] pt-4">
            <FormField
              control={form.control}
              name="routeNamespaceId"
              render={({ field }) => (
                <FormItem>
                  <FormDescription className="text-xs mb-2">
                    Select the bridge that provides access to this device's network.
                  </FormDescription>
                  <FormControl>
                    <BridgeRouteSelector
                      value={field.value}
                      onChange={(value) => field.onChange(value ?? '')}
                      placeholder="Select an EdgeLink bridge..."
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Site default indicator/button */}
            {hasSelectedRoute && showSetSiteDefault && (
              <div className="flex items-center gap-2">
                {isCurrentSiteDefault ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs gap-1.5 bg-lime/10 text-lime border-lime/30 hover:bg-red-500/10 hover:text-red-400 hover:border-red-400/30 transition-colors group"
                    onClick={handleClearSiteDefault}
                    disabled={clearSiteDefaultMutation.isPending}
                  >
                    {clearSiteDefaultMutation.isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Removing...
                      </>
                    ) : (
                      <>
                        <Check className="h-3 w-3 group-hover:hidden" />
                        <X className="h-3 w-3 hidden group-hover:block" />
                        <span>Site default</span>
                      </>
                    )}
                  </Button>
                ) : canSetAsDefault ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={handleSetAsDefault}
                    disabled={setSiteDefaultMutation.isPending}
                  >
                    {setSiteDefaultMutation.isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                        Setting...
                      </>
                    ) : (
                      'Set as site default'
                    )}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
